import { AnnotationTriageArea } from '@admin-panel/annotation-triage'
import { ApPricingArea } from '@admin-panel/ap-pricing'
import { ApServiceSettingsArea } from '@admin-panel/ap-service-settings'
import { DisengagementVoicebotArea } from '@admin-panel/disengagement-voicebot'
import { EvaluationAiArea } from '@admin-panel/evaluation-ai'
import { FleetPlannerArea } from '@admin-panel/fleet-planner'
import { GlobalVehicleManagementArea } from '@admin-panel/global-vehicle-management'
import { HulkTripsArea } from '@admin-panel/hulk-trips'

import { IncidentAnalysisArea } from '@admin-panel/incident-analysis'
import { MapInspectorArea } from '@admin-panel/map-inspector'
import { RouteBuilderArea } from '@admin-panel/route-builder'
import { SafetyDriverAnnotationsArea } from '@admin-panel/safety-driver-annotations'
import { TemporaryBackofficeSessionsArea } from '@admin-panel/temporary-backoffice-sessions'
import { TripHistoryArea } from '@admin-panel/trip-history'
import { UserManagementArea } from '@admin-panel/user-management'
import { VehicleDataOverrideArea } from '@admin-panel/vehicle-data-override'
import { VehicleMonitoringArea } from '@admin-panel/vehicle-monitoring'
import { VirtualTestingArea } from '@admin-panel/virtual-testing'
import type { SectionsConfig } from '@backoffice-frontend/authenticated-app-view'
import { isProduction } from '@backoffice-frontend/environment'
import {
  Features,
  useIsFeatureToggleEnabled,
} from '@backoffice-frontend/feature-toggle'
import { PatternLibraryArea } from '@backoffice-frontend/pattern-library'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { StatusArea } from '@backoffice-frontend/status'
import { WelcomeAdminPanelArea } from '@backoffice-frontend/welcome-admin-panel'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { UpdatesArea } from '@shared/updates'
import { useTranslation } from 'react-i18next'

export const useNavigationSections = (): SectionsConfig => {
  const { t } = useTranslation()

  const canAccessUserManagement = useUserHasAccess({
    allowedRights: [BoumRight.ADMIN_PANEL_USER_MANAGEMENT_CRUD],
  })

  const isTripHistoryEnabled = useIsFeatureToggleEnabled(Features.TRIP_HISTORY)
  const isEvaluationAiEnabled = useIsFeatureToggleEnabled(
    Features.EVALUATION_AI,
  )

  const isVehicleMonitoringEnabled = useIsFeatureToggleEnabled(
    Features.VEHICLE_MONITORING,
  )

  const isHulkTripsEnabled = useIsFeatureToggleEnabled(Features.HULK_TRIPS)

  const isAnnotationTriageEnabled = useIsFeatureToggleEnabled(
    Features.ANNOTATION_TRIAGE,
  )

  const isGlobalVehicleManagementEnabled = useIsFeatureToggleEnabled(
    Features.GLOBAL_VEHICLE_MANAGEMENT,
  )
  const isDisengagementVoicebotEnabled = useIsFeatureToggleEnabled(
    Features.DISENGAGEMENT_VOICEBOT,
  )

  const sectionConfig: SectionsConfig = [
    // Route-only areas (hidden from navigation bar)
    {
      areas: [WelcomeAdminPanelArea, StatusArea],
      hiddenInNavBar: true,
    },
    {
      areas: [
        ApServiceSettingsArea,
        IncidentAnalysisArea,
        ApPricingArea,
        UpdatesArea,
      ],
    },
    {
      label: t('adminPanelApp_goesTools_navigation'),
      areas: [
        TemporaryBackofficeSessionsArea,
        isGlobalVehicleManagementEnabled ? GlobalVehicleManagementArea : null,
      ],
    },
    // Fleet Planner is on hold and broken on PRD, so it is only kept on DEV/INT
    !isProduction()
      ? {
          label: t('adminPanelApp_fleetPlanner_navigation'),
          areas: [FleetPlannerArea],
        }
      : null,
    !isProduction()
      ? {
          label: t('adminPanelApp_testingSection_navigation'),
          areas: [PatternLibraryArea],
        }
      : null,
    {
      label: t('adminPanelApp_vnv_section_navigation'),
      areas: [
        VirtualTestingArea,
        SafetyDriverAnnotationsArea,
        VehicleDataOverrideArea,
      ],
    },
    {
      label: t('adminPanelApp_tmp_section_navigation'),
      areas: [
        isVehicleMonitoringEnabled ? VehicleMonitoringArea : null,
        RouteBuilderArea,
        isAnnotationTriageEnabled ? AnnotationTriageArea : null,
        isHulkTripsEnabled ? HulkTripsArea : null,
        isTripHistoryEnabled ? TripHistoryArea : null,
        isEvaluationAiEnabled ? EvaluationAiArea : null,
        isDisengagementVoicebotEnabled ? DisengagementVoicebotArea : null,
      ],
    },
    {
      label: t('adminPanelApp_maps_section_navigation'),
      areas: [MapInspectorArea],
    },
    // User Management must always be the LAST visible navigation section.
    // See useNavigationSections.unit.test.tsx for the enforcing test.
    {
      label: t('adminPanelApp_adminSection_navigation'),
      areas: [UserManagementArea],
      hiddenInNavBar: !canAccessUserManagement,
    },
  ]

  return sectionConfig
}
